<template>
  <div>
    <div v-if="!loading">
      <div class="legend">
        <ProjectComposition
          :composition="legend"
          :hide-pie="true"
          @legendSelectChanged="blurCalendarEvents"
        />
      </div>
      <div v-if="extraInfo.show" class="extraInfo">
        <transition name="fade">
          <p>
            <span>{{
              `${extraInfo.reason} ${extraInfo.reason.length > 45 ? "..." : ""}`
            }}</span>
          </p>
        </transition>
      </div>
      <!-- Management Leave Calendar -->
      <FullCalendar
        v-if="userIsSuperAdmin"
        ref="leaveCalendar"
        default-view="dayGridMonth"
        :header="{ left: 'title', center: '', right: 'prev,next' }"
        height="auto"
        :plugins="calendarPlugins"
        :events="leaveEvents"
        :day-render="onDayRender"
        @eventMouseEnter="eventHoverEnter"
        @eventMouseLeave="eventHoverLeave"
        @eventClick="onEventClicked"
      />
      <!-- Leave Calendar -->
      <FullCalendar
        v-else
        ref="leaveCalendar"
        default-view="dayGridMonth"
        :header="{ left: 'title', center: '', right: 'prev,next' }"
        :first-day="1"
        height="auto"
        :plugins="calendarPlugins"
        :events="leaveEvents"
        :day-render="onDayRender"
        @eventMouseEnter="eventHoverEnter"
        @eventMouseLeave="eventHoverLeave"
      />
    </div>
  </div>
</template>
<script>
import FullCalendar from "@fullcalendar/vue"
import dayGridPlugin from "@fullcalendar/daygrid"
import ProjectComposition from "@/components/userdashboard/projectComposition"
import { mapGetters } from "vuex"
import debounce from "debounce"

import "@fullcalendar/core/main.css"
import "@fullcalendar/daygrid/main.css"
import "@/assets/styles/calendar/calendar_custom.css"

export default {
  name: "LeaveCalendar",
  components: {
    FullCalendar,
    ProjectComposition
  },
  props: {
    colourByProject: {
      type: Boolean,
      default: false
    },
    colourPalette: {
      type: Array,
      default: null
    },
    legend: {
      type: Object,
      default: null
    }
  },
  apollo: {
    leaveItems: {
      query: require("@/graphql/Leave/GetLeaveItems.gql"),
      update(data) {
        this.loading = false
        return data.getLeaveItems
      }
    }
  },
  data() {
    return {
      loading: true,
      calendarPlugins: [dayGridPlugin],
      colours: this.colourPalette
        ? this.colourPalette
        : ["#89C3CA", "#C6EC8E", "#FFCB4B", "#E84583", "#AD33B9"],
      blurredEvents: [],
      extraInfo: {
        show: false,
        reason: null
      },
      leaveCalendar: null
    }
  },
  computed: {
    ...mapGetters({
      getCurrentUser: "getCurrentUser",
      getUserById: "getUserById"
    }),
    // leaveCalendar() {
    //   return this.$refs.leaveCalendar?.getApi()
    //   // if (leaveCalendar) {
    //
    //   // }
    //   // return leaveCalendar
    // },
    requestDate() {
      return this.$route.params.date
    },
    userIsSuperAdmin() {
      return (
        this.getCurrentUser &&
        this.getCurrentUser.roles &&
        this.getCurrentUser.roles.includes("Super Admin")
      )
    },
    leaveEvents() {
      let eventsList = []
      this.leaveItems.forEach(item => {
        //Only SuperAdmins see unapproved leave
        //No one sees rejected leave
        if (
          (!this.userIsSuperAdmin &&
            !item.approved &&
            !item.type == "WorkFromHome") ||
          item.rejected ||
          item.user == null
        )
          return

        let type = this.blurredEvents.find(
          x => this.$utils.removeWhitespace(x.name) === item.type
        )
        item.blurred = type ? type.blur : false

        // let user = this.getUserById(item.userId)
        // if (!user) return
        let event = {
          id: item.id,
          title: this.setTitle(
            item.user,
            item.approved || item.type === "WorkFromHome"
          ),
          //if sick leave doesnt need to be approved which it shouldn't really
          // title: this.setTitle(user, item.approved || item.type === "SickLeave"),

          start: new Date(new Date(item.from).toISOString()),
          end: new Date(this.$utils.addDays(item.to, 0).toISOString()),
          textColor: "black",
          editable: true,
          backgroundColor: this.getColourByType(item.type),
          allDay: true,
          classNames: [`${item.blurred ? "opacity-20-p" : ""}`],
          extendedProps: item
        }
        eventsList.push(event)
      })

      return eventsList
    }
  },
  mounted() {
    this.setLeaveCalendar()
  },
  methods: {
    /**
     * Sets the leaveCalendar data value.
     * Debounced and recursive for inconsistent loading times
     */
    setLeaveCalendar: debounce(function() {
      this.leaveCalendar = this.$refs?.leaveCalendar?.getApi()

      if (!this.leaveCalendar) this.setLeaveCalendar()
      else {
        //If month specified in url
        //..Move calendar to that month
        const month = this.$route.params.month
        if (month && month >= 1 && month <= 12) {
          this.leaveCalendar.gotoDate(
            new Date(new Date().setMonth(month - 1)).toISOString()
          )
        }
      }
    }, 1000),
    /**
     *
     * @param date
     * @param numberOfDays
     * @returns {number}
     */
    addDays(date, numberOfDays) {
      return date.setDate(date.getDate() + numberOfDays)
    },
    /**
     * Greys out weekends
     * @param info
     */
    onDayRender(info) {
      if (
        info.date.getDay() === 0 ||
        info.date.getDay() === 6 ||
        info.date < new Date()
      ) {
        info.el.classList.add("fc-weekend")
      }
    },
    /**
     * Shows leave reason above calendar
     * @param info
     */
    eventHoverEnter(info) {
      document.getElementsByClassName("fc-toolbar")[0].style.display = "none"
      this.extraInfo.reason =
        info.event.extendedProps.reason ?? "Working From Home"
      this.extraInfo.show = true
    },
    eventHoverLeave() {
      document.getElementsByClassName("fc-toolbar")[0].style.display = "flex"
      this.extraInfo.show = false
    },
    /**
     * Hides leave reason above calendar
     * @param info
     */
    onEventClicked(info) {
      let leaveItem = info.event.extendedProps
      let user = leaveItem.user

      // if (!leaveItem.approved && !leaveItem.type == "WorkFromHome")
      if (!leaveItem.approved) this.approveModal(leaveItem, user)
      // else if (!leaveItem.type == "WorkFromHome")
      else this.rejectModal(leaveItem, user)
    },
    /**
     * Shows approve modal
     * @param leaveItem
     * @param user
     */
    approveModal(leaveItem, user) {
      this.$bvModal
        .msgBoxConfirm(
          `Approve ${this.getleaveType(leaveItem.type)} for ${user.firstName} ${
            user.lastName
          }? from ${this.$utils.getDate(
            leaveItem.from
          )} to ${this.$utils.getDate(leaveItem.to)}?`,
          {
            title: "Approval",
            size: "lg",
            buttonSize: "lg",
            okVariant: "success",
            headerClass: "p-2 border-bottom-0 ",
            footerClass: "p-2 border-top-0 ",
            centered: true,
            okTitle: "Approve",
            cancelTitle: "Reject",
            cancelVariant: "danger"
          }
        )
        .then(async value => {
          if (value) {
            await this.leaveApproved(await this.approveLeaveItem(leaveItem.id))
          } else if (value === false) {
            await this.leaveRejected(await this.rejectLeaveItem(leaveItem.id))
          }
        })
        .catch(() => {
          this.$toasted.error("An error occured")
        })
    },
    rejectModal(leaveItem, user) {
      this.$bvModal
        .msgBoxConfirm(
          `Reject previously approved ${this.getleaveType(
            leaveItem.type
          )} for ${user.firstName} ${user.lastName}? from ${this.$utils.getDate(
            leaveItem.from
          )} to ${this.$utils.getDate(leaveItem.to)}?`,
          {
            title: "Reject",
            size: "lg",
            buttonSize: "lg",
            okVariant: "danger",
            headerClass: "p-2 border-bottom-0",
            footerClass: "p-2 border-top-0",
            centered: true,
            okTitle: "Reject",
            cancelTitle: "Cancel",
            cancelVariant: "success"
          }
        )
        .then(async value => {
          if (value)
            await this.leaveRejected(await this.rejectLeaveItem(leaveItem.id))
        })
        .catch(error => {
          console.log(error)
          this.$toasted.error("An error occured")
        })
    },
    getColourByType(type) {
      const colours = {
        Leave: "#AD4FFC",
        SickLeave: "#FDD835",
        StudyLeave: "#0277BD",
        WorkFromHome: "#07dbd7",
        default: "#AD4FFC"
      }
      return colours[type] || colours["default"]
    },
    setTitle(user, approved) {
      let alarm = !approved ? `❗` : ""
      return `${alarm} ${user.firstName} ${user.lastName.substr(0, 1)}`
    },
    blurCalendarEvents(selections) {
      let mappedSelections = []
      Object.keys(selections).map(function(key) {
        mappedSelections.push({
          name: key,
          blur: !selections[key]
        })
      })
      this.blurredEvents = mappedSelections
    },
    getleaveType(leave) {
      const leaveTypes = {
        Leave: "Leave",
        SickLeave: "Sick Leave",
        StudyLeave: "Study leave",
        default: "Leave"
      }
      return leaveTypes[leave] || leaveTypes["default"]
    },
    async approveLeaveItem(leaveId) {
      const response = await this.$apollo.mutate({
        mutation: require("@/graphql/Leave/ApproveLeave.gql"),
        variables: {
          leave: {
            id: leaveId
          }
        }
      })
      return response.data.approveLeave
    },
    async rejectLeaveItem(leaveId) {
      const response = await this.$apollo.mutate({
        mutation: require("@/graphql/Leave/RejectLeave.gql"),
        variables: {
          leave: {
            id: leaveId
          }
        }
      })
      return response.data.rejectLeave
    },
    /**
     *
     * @param leaveItem
     * @returns {Promise<void>}
     */
    async leaveApproved(leaveItem) {
      Object.assign(
        this.leaveItems.find(x => x.id == leaveItem.id),
        leaveItem
      )
      this.$toasted.success("Leave approved")
    },
    /**
     *
     * @param leaveItem
     * @returns {Promise<void>}
     */
    async leaveRejected(leaveItem) {
      this.lodash.remove(this.leaveItems, item => {
        return item.id === leaveItem.id
      })
      //Needed for calendar to update state
      this.leaveItems = this.leaveItems.map(x => x)
      this.$toasted.success("Leave rejected")
    }
  }
}
</script>
<style scoped>
.extraInfo {
  background-color: #2f4554;
  border-radius: 5px;
  color: white;
  padding: 2px;
  margin-bottom: 5px;
  padding-left: 10px;
  height: 28px;
}
.extraInfo p {
  margin-bottom: 0px;
}
.extraInfo .type {
  font-weight: bold;
}
.legend {
  margin-top: -50px;
  height: 45px;
}

.modal-content {
  background-color: #fff !important;
  font: 12px "Microsoft YaHei" !important;
}

.modal-title {
  font: 20px "Microsoft YaHei" !important;
}

/* .today-btn {
  float: right;
  margin-bottom: 10px;
  cursor: pointer;
} */
</style>

<style>
.modal-content {
  background: whitesmoke !important;
}
</style>
